import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "startInput", "endInput", "startDisplay", "endDisplay", "valueDisplay"]
  static values = { mode: String, placeholder: { type: String, default: "Select date" } }

  connect() {
    this.selectedDate = null
    this.rangeStart = null
    this.rangeEnd = null
    this.selectingStart = true

    // Find parent dropdown controller
    // In portal mode, the menu is moved to body, so we need to find the dropdown differently
    this.findDropdownElement()

    // Initialize from hidden inputs if they have values
    if (this.modeValue === "range") {
      if (this.hasStartInputTarget && this.startInputTarget.value) {
        this.rangeStart = new Date(this.startInputTarget.value)
      }
      if (this.hasEndInputTarget && this.endInputTarget.value) {
        this.rangeEnd = new Date(this.endInputTarget.value)
      }
      // If we have both dates, next selection should start fresh
      if (this.rangeStart && this.rangeEnd) {
        this.selectingStart = true
      }
    } else {
      if (this.hasInputTarget && this.inputTarget.value) {
        this.selectedDate = new Date(this.inputTarget.value)
      }
    }
  }

  findDropdownElement() {
    // First try to find it as a parent (non-portal mode)
    this.dropdownElement = this.element.closest("[data-controller*='dropdown']")

    // If not found directly, try parent elements
    if (!this.dropdownElement) {
      let parent = this.element.parentElement
      while (parent && !this.dropdownElement) {
        if (parent.dataset && parent.dataset.controller && parent.dataset.controller.includes("dropdown")) {
          this.dropdownElement = parent
        }
        parent = parent.parentElement
      }
    }

    // For portal mode: the menu (this.element's parent) is the dropdown menu target
    // We need to find which dropdown controller owns this menu
    if (!this.dropdownElement) {
      // Find all dropdown controllers and check which one has this element in its menu
      const allDropdowns = document.querySelectorAll("[data-controller*='dropdown']")
      for (const dropdown of allDropdowns) {
        const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown")
        if (controller) {
          const menu = controller.getMenuElement ? controller.getMenuElement() : controller.menuTarget
          if (menu && menu.contains(this.element)) {
            this.dropdownElement = dropdown
            this.dropdownController = controller
            break
          }
        }
      }
    }
  }

  closeDropdown() {
    // Use cached controller if available (for portal mode)
    if (this.dropdownController) {
      this.dropdownController.close()
      return
    }

    if (this.dropdownElement) {
      const dropdownController = this.application.getControllerForElementAndIdentifier(
        this.dropdownElement,
        "dropdown"
      )
      if (dropdownController) {
        dropdownController.close()
      }
    }
  }

  updateTriggerText(text) {
    if (this.dropdownElement) {
      const triggerText = this.dropdownElement.querySelector("[data-dropdown-target='triggerText']")
      if (triggerText) {
        triggerText.textContent = text
      }
    }
  }

  selectDate(event) {
    event.preventDefault()
    event.stopPropagation()

    const dateStr = event.currentTarget.dataset.date
    if (!dateStr) return

    const date = new Date(dateStr)
    const cell = event.currentTarget

    // Check if cell is disabled
    if (cell.classList.contains("cursor-not-allowed")) return

    if (this.modeValue === "range") {
      if (this.selectingStart || (this.rangeEnd && date > this.rangeEnd)) {
        // Clear all highlights when starting a new selection
        this.clearAllHighlights()
        this.rangeStart = date
        this.rangeEnd = null
        this.selectingStart = false
      } else {
        if (date < this.rangeStart) {
          this.rangeEnd = this.rangeStart
          this.rangeStart = date
        } else {
          this.rangeEnd = date
        }
        this.selectingStart = true
      }
      this.updateRangeDisplay()
      this.highlightRange()
    } else {
      this.selectedDate = date
      this.updateSingleDisplay()
      this.highlightSelected(cell)
    }
  }

  selectToday() {
    const today = new Date()
    this.selectedDate = today
    this.navigateCalendarsToDate(today)
    this.updateSingleDisplay()

    // Find and highlight today's cell
    const todayStr = this.formatDateISO(today)
    const cell = this.element.querySelector(`[data-date="${todayStr}"]`)
    if (cell) {
      this.highlightSelected(cell)
    }
  }

  selectPreset(event) {
    const preset = event.currentTarget.dataset.preset
    const today = new Date()

    // Remove active state from all preset buttons
    const presetButtons = this.element.querySelectorAll("[data-preset]")
    presetButtons.forEach(btn => btn.classList.remove("bg-[#FAFAFB]"))

    // Add active state to selected preset
    event.currentTarget.classList.add("bg-[#FAFAFB]")

    switch (preset) {
      case "0": // Today
        this.rangeStart = new Date(today)
        this.rangeEnd = new Date(today)
        break
      case "-1": // Yesterday
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        this.rangeStart = yesterday
        this.rangeEnd = new Date(yesterday)
        break
      case "this_week":
        this.rangeStart = this.startOfWeek(today)
        this.rangeEnd = new Date(today)
        break
      case "last_week":
        const lastWeekDate = new Date(today)
        lastWeekDate.setDate(lastWeekDate.getDate() - 7)
        this.rangeStart = this.startOfWeek(lastWeekDate)
        this.rangeEnd = this.endOfWeek(lastWeekDate)
        break
      case "this_month":
        this.rangeStart = new Date(today.getFullYear(), today.getMonth(), 1)
        this.rangeEnd = new Date(today)
        break
      case "last_month":
        this.rangeStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        this.rangeEnd = new Date(today.getFullYear(), today.getMonth(), 0)
        break
      case "this_year":
        this.rangeStart = new Date(today.getFullYear(), 0, 1)
        this.rangeEnd = new Date(today)
        break
      case "last_year":
        this.rangeStart = new Date(today.getFullYear() - 1, 0, 1)
        this.rangeEnd = new Date(today.getFullYear() - 1, 11, 31)
        break
    }

    // Navigate calendars to show the selected range
    if (this.rangeStart) {
      this.navigateCalendarsToDate(this.rangeStart)
    }

    this.updateRangeDisplay()
    this.highlightRange()
  }

  apply(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.modeValue === "range") {
      if (this.hasStartInputTarget && this.rangeStart) {
        this.startInputTarget.value = this.formatDateISO(this.rangeStart)
      }
      if (this.hasEndInputTarget && this.rangeEnd) {
        this.endInputTarget.value = this.formatDateISO(this.rangeEnd)
      }
      if (this.rangeStart && this.rangeEnd) {
        this.updateTriggerText(`${this.formatDate(this.rangeStart)} - ${this.formatDate(this.rangeEnd)}`)
      }
    } else {
      if (this.hasInputTarget && this.selectedDate) {
        this.inputTarget.value = this.formatDateISO(this.selectedDate)
      }
      if (this.selectedDate) {
        this.updateTriggerText(this.formatDate(this.selectedDate))
      }
    }

    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent("date-picker:apply", {
      detail: {
        mode: this.modeValue,
        selectedDate: this.selectedDate,
        rangeStart: this.rangeStart,
        rangeEnd: this.rangeEnd
      },
      bubbles: true
    }))

    this.closeDropdown()
  }

  clear(event) {
    this.cancel(event)
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.updateTriggerText(this.placeholderValue)
    this.element.dispatchEvent(new CustomEvent("date-picker:apply", {
      detail: { mode: this.modeValue, selectedDate: null, rangeStart: null, rangeEnd: null },
      bubbles: true
    }))
  }

  cancel(event) {
    event.preventDefault()
    event.stopPropagation()

    // Reset selections
    this.selectedDate = null
    this.rangeStart = null
    this.rangeEnd = null
    this.selectingStart = true

    // Clear highlights and reset displays
    this.clearAllHighlights()

    // Reset display values
    if (this.hasValueDisplayTarget) {
      this.valueDisplayTarget.textContent = "Select date"
    }
    if (this.hasStartDisplayTarget) {
      this.startDisplayTarget.textContent = "Start date"
    }
    if (this.hasEndDisplayTarget) {
      this.endDisplayTarget.textContent = "End date"
    }

    this.closeDropdown()
  }

  // Highlight methods
  highlightSelected(cell) {
    // Clear previous selection
    const allCells = this.element.querySelectorAll("[data-date]")
    const today = new Date()
    const todayStr = this.formatDateISO(today)

    allCells.forEach(c => {
      // Restore original styles based on cell type
      const isToday = c.dataset.date === todayStr
      const isInactive = c.classList.contains("text-gray-600")

      // Remove selected styles
      c.classList.remove("bg-brand-600", "hover:bg-brand-700", "text-white", "rounded-full", "group")

      if (isToday) {
        // Restore today styles - remove old classes first, then add correct ones
        c.classList.remove("font-medium", "hover:bg-gray-200", "bg-gray-50")
        c.classList.add("text-gray-900", "font-medium", "bg-gray-50", "hover:bg-gray-200", "rounded-full")
      } else if (!isInactive) {
        // Restore active styles
        c.classList.remove("font-medium", "hover:bg-gray-200", "bg-gray-50")
        c.classList.add("text-gray-800", "hover:bg-gray-50", "rounded-full")
      }

      // Restore dot color to default (dot is inside the cell)
      const dot = c.querySelector(".absolute.bottom-1")
      if (dot) {
        dot.classList.remove("bg-white")
        dot.classList.add("bg-brand-600")
      }
    })

    // Highlight selected cell
    cell.classList.add("bg-brand-600", "hover:bg-brand-700", "text-white", "font-medium", "rounded-full", "group")
    cell.classList.remove("text-gray-800", "text-gray-900", "hover:bg-gray-50", "hover:bg-gray-200", "bg-gray-50")

    // Change dot color to white when selected (dot is inside the cell)
    // Also ensure it stays white on hover
    const dot = cell.querySelector(".absolute.bottom-1")
    if (dot) {
      dot.classList.remove("bg-brand-600")
      dot.classList.add("bg-white")
    }
  }

  highlightRange() {
    if (!this.rangeStart) return

    const allCells = this.element.querySelectorAll("[data-date]")
    const today = new Date()
    const todayStr = this.formatDateISO(today)

    allCells.forEach(cell => {
      const dateStr = cell.dataset.date
      if (!dateStr) return

      const cellDate = new Date(dateStr)
      const wrapper = cell.parentElement
      const isToday = dateStr === todayStr
      const isInactive = cell.classList.contains("text-gray-600")

      // Clear previous range styles
      cell.classList.remove("bg-brand-600", "hover:bg-brand-700", "hover:bg-gray-200", "text-white", "bg-gray-50", "z-10", "font-medium", "relative", "group", "rounded-full")
      wrapper.querySelectorAll(".absolute.-z-10").forEach(el => el.remove())

      // Restore default styles based on cell type
      if (isToday) {
        cell.classList.add("text-gray-900", "font-medium", "bg-gray-50", "hover:bg-gray-200", "rounded-full")
      } else if (!isInactive) {
        cell.classList.add("text-gray-800", "hover:bg-gray-50", "rounded-full")
      }

      // Reset dot color to default (dot is inside the cell)
      const dot = cell.querySelector(".absolute.bottom-1")
      if (dot) {
        dot.classList.remove("bg-white")
        dot.classList.add("bg-brand-600")
      }

      // Check if this date is in range
      const isStart = this.rangeStart && this.isSameDay(cellDate, this.rangeStart)
      const isEnd = this.rangeEnd && this.isSameDay(cellDate, this.rangeEnd)
      const isInRange = this.rangeStart && this.rangeEnd &&
                        cellDate > this.rangeStart && cellDate < this.rangeEnd

      if (isStart || isEnd) {
        // Selected (start or end)
        cell.classList.add("bg-brand-600", "hover:bg-brand-700", "text-white", "font-medium", "z-10", "relative", "rounded-full", "group")
        cell.classList.remove("text-gray-800", "text-gray-900", "hover:bg-gray-50", "hover:bg-gray-200", "bg-gray-50")

        // Change dot color to white when selected (dot is inside the cell)
        const selectedDot = cell.querySelector(".absolute.bottom-1")
        if (selectedDot) {
          selectedDot.classList.remove("bg-brand-600")
          selectedDot.classList.add("bg-white")
        }

        // Add connector (check if not at row edge)
        if (this.rangeEnd) {
          const dayOfWeek = cellDate.getDay() // 0 = Sunday, 6 = Saturday
          const isEndOfRow = dayOfWeek === 0 // Sunday is last day of row (Mo-Su)
          const isStartOfRow = dayOfWeek === 1 // Monday is first day of row

          if (isStart && !this.isSameDay(this.rangeStart, this.rangeEnd) && !isEndOfRow) {
            this.addConnector(wrapper, "right")
          }
          if (isEnd && !this.isSameDay(this.rangeStart, this.rangeEnd) && !isStartOfRow) {
            this.addConnector(wrapper, "left")
          }
        }
      } else if (isInRange) {
        // In range (middle) - active state with hover
        cell.classList.add("bg-gray-50", "hover:bg-gray-200", "font-medium", "relative", "z-10", "rounded-full")
        cell.classList.remove("hover:bg-gray-50")

        const dayOfWeek = cellDate.getDay()
        const isEndOfRow = dayOfWeek === 0 // Sunday
        const isStartOfRow = dayOfWeek === 1 // Monday

        if (!isStartOfRow) {
          this.addConnector(wrapper, "left")
        }
        if (!isEndOfRow) {
          this.addConnector(wrapper, "right")
        }
      }
    })
  }

  navigateCalendarsToDate(startDate) {
    // Find all calendar controllers in this date picker
    const calendarElements = this.element.querySelectorAll("[data-controller*='calendar']")

    if (calendarElements.length === 2) {
      // Dual calendar mode - left shows start month, right shows next consecutive month
      const leftCalendar = calendarElements[0]
      const rightCalendar = calendarElements[1]

      const leftController = this.application.getControllerForElementAndIdentifier(leftCalendar, "calendar")
      const rightController = this.application.getControllerForElementAndIdentifier(rightCalendar, "calendar")

      if (leftController && rightController) {
        // Set left calendar to start date month
        leftController.monthValue = startDate.getMonth() + 1
        leftController.yearValue = startDate.getFullYear()
        leftController.refreshCalendar()

        // Right calendar always shows the month after left calendar (never same month)
        if (startDate.getMonth() === 11) {
          rightController.monthValue = 1
          rightController.yearValue = startDate.getFullYear() + 1
        } else {
          rightController.monthValue = startDate.getMonth() + 2
          rightController.yearValue = startDate.getFullYear()
        }
        rightController.refreshCalendar()
      }
    } else if (calendarElements.length === 1) {
      // Single calendar mode
      const calendarController = this.application.getControllerForElementAndIdentifier(calendarElements[0], "calendar")
      if (calendarController) {
        calendarController.monthValue = startDate.getMonth() + 1
        calendarController.yearValue = startDate.getFullYear()
        calendarController.refreshCalendar()
      }
    }
  }

  addConnector(wrapper, side) {
    const connector = document.createElement("div")
    connector.className = `absolute w-10 h-10 top-0 bg-gray-50 -z-10 ${side === "left" ? "-left-5" : "-right-5"}`
    wrapper.appendChild(connector)
  }

  clearAllHighlights() {
    const allCells = this.element.querySelectorAll("[data-date]")
    allCells.forEach(cell => {
      cell.classList.remove("bg-brand-600", "hover:bg-brand-700", "hover:bg-gray-200", "text-white", "bg-gray-50", "z-10", "font-medium", "rounded-full", "group")
      cell.classList.add("hover:bg-gray-50")
      // Restore default text color
      if (!cell.classList.contains("text-gray-600")) {
        cell.classList.add("text-gray-800")
      }
      const wrapper = cell.parentElement
      wrapper.querySelectorAll(".absolute:not([class*='bottom'])").forEach(el => el.remove())

      // Restore dot color to default (dot is inside the cell)
      const dot = cell.querySelector(".absolute.bottom-1")
      if (dot) {
        dot.classList.remove("bg-white")
        dot.classList.add("bg-brand-600")
      }
    })
  }

  // Helper methods
  updateSingleDisplay() {
    if (this.hasValueDisplayTarget && this.selectedDate) {
      this.valueDisplayTarget.textContent = this.formatDate(this.selectedDate)
    }
  }

  updateRangeDisplay() {
    if (this.hasStartDisplayTarget) {
      this.startDisplayTarget.textContent = this.rangeStart ? this.formatDate(this.rangeStart) : "Start date"
    }
    if (this.hasEndDisplayTarget) {
      this.endDisplayTarget.textContent = this.rangeEnd ? this.formatDate(this.rangeEnd) : "End date"
    }
  }

  formatDate(date) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
  }

  formatDateISO(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  isSameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate()
  }

  startOfWeek(date) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  }

  endOfWeek(date) {
    const start = this.startOfWeek(new Date(date))
    return new Date(start.setDate(start.getDate() + 6))
  }
}
